/**
 * Derive granular "What's New" activity events for a client from a hiring
 * position and its linked position request.
 *
 * A position request (`positionRequests/{id}`) stores the fulfillment activity
 * the HR team performs as arrays/maps on the doc: `candidates[]`, `notes[]`, and
 * `timelineSteps{}`. This flattens that into individually dated feed events so
 * the portal HR "What's New" card can surface, for each request:
 *   - a timeline step being completed
 *   - a new candidate being added
 *   - a new note that did NOT come from the viewing client
 *   - a candidate being favorited (by someone other than the viewing client)
 *   - a candidate status change (moved forward / passed on / stage advanced)
 *
 * Events are only emitted when a usable timestamp exists, so activity that
 * predates timestamp stamping simply doesn't appear (rather than sorting wrong).
 */

const CANDIDATE_STEP_LABELS = {
  applied: 'Applied',
  'pre-screen-interview': 'Pre-Screen Interview',
  'company-interview': 'Company Interview',
  'decision-offer': 'Decision & Offer',
};

const TIMELINE_STEP_LABELS = {
  'define-requirements': 'Role Requirements Defined',
  'create-posting': 'Posting Created',
  'screen-applicants': 'Applicants Screened',
  'pre-screen-interview': 'Pre-Screen Interview',
  'company-interview': 'Company Interview',
  'decision-offer': 'Decision & Offer',
};

const humanizeStep = (stepId, labels) =>
  labels[stepId] ||
  String(stepId || '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

/** Parse an ISO string, epoch, YYYY-MM-DD, or Firestore Timestamp into a Date (or null). */
const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * @param {object} request  The positionRequests doc data.
 * @param {object} position The positions doc data (must include `id`).
 * @param {string|null} currentUserUid  The viewing client's uid, used to exclude their own notes/favorites.
 * @returns {Array<{ id: string, kind: 'hiring', activityType: string, title: string, subtitle: string, date: Date, path: string }>}
 */
export function deriveHiringActivityEvents(request, position, currentUserUid) {
  if (!request || !position?.id) return [];

  const positionTitle = position.title || request.position || 'Position';
  const path = `/portal/hr/hiring/position/${position.id}`;
  const events = [];

  const push = (id, activityType, title, date) => {
    events.push({ id, kind: 'hiring', activityType, title, subtitle: positionTitle, date, path });
  };

  // Candidates: added / favorited / status change / stage advance.
  (Array.isArray(request.candidates) ? request.candidates : []).forEach((c) => {
    if (!c) return;
    const cid = c.id != null ? String(c.id) : `anon-${Math.random()}`;
    const name = c.name || 'Candidate';

    const addedDate = toDate(c.createdAt) || toDate(c.appliedDate);
    if (addedDate) push(`cand-add-${position.id}-${cid}`, 'candidate', `New candidate: ${name}`, addedDate);

    // Favorited by someone other than the viewing client.
    const favoritedByClient = c.favoritedBy && currentUserUid && c.favoritedBy === currentUserUid;
    if (c.isFavorite && !favoritedByClient) {
      const favDate = toDate(c.favoritedAt);
      if (favDate) push(`cand-fav-${position.id}-${cid}`, 'favorite', `${name} was favorited`, favDate);
    }

    if (c.actionStatus === 'continue') {
      const d = toDate(c.continuedAt);
      if (d) push(`cand-cont-${position.id}-${cid}`, 'status', `${name} is moving forward`, d);
    } else if (c.actionStatus === 'rejected') {
      const d = toDate(c.rejectedAt);
      if (d) push(`cand-rej-${position.id}-${cid}`, 'status', `${name} was passed on`, d);
    }

    Object.entries(c.processSteps || {}).forEach(([stepId, sd]) => {
      if (!sd?.completed) return;
      const d = toDate(sd.completedAt) || toDate(sd.completedDate);
      if (d) {
        push(
          `cand-step-${position.id}-${cid}-${stepId}`,
          'status',
          `${name}: ${humanizeStep(stepId, CANDIDATE_STEP_LABELS)}`,
          d
        );
      }
    });
  });

  // Notes that did not come from the viewing client (HR / system updates).
  (Array.isArray(request.notes) ? request.notes : []).forEach((n) => {
    if (!n) return;
    const fromClient = n.author?.uid && currentUserUid && n.author.uid === currentUserUid;
    if (fromClient) return;
    const d = toDate(n.timestamp) || toDate(n.date);
    if (!d) return;
    const who = n.author?.name || 'your HR team';
    push(`note-${position.id}-${n.id}`, 'note', `New note from ${who}`, d);
  });

  // Timeline steps completed by the HR team.
  Object.entries(request.timelineSteps || {}).forEach(([stepId, sd]) => {
    if (!sd?.completed) return;
    const d = toDate(sd.completedAt) || toDate(sd.completedDate);
    if (d) push(`tl-${position.id}-${stepId}`, 'timeline', `Timeline: ${humanizeStep(stepId, TIMELINE_STEP_LABELS)}`, d);
  });

  return events;
}
